import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserRound, Lock, Bell } from 'lucide-react';
import { toast } from 'react-toastify';
import CustomInput from '../../../components/ui/CustomInput';
import CountrySelectInput from '../../../components/ui/CountrySelect';
import StateSelectInput from '../../../components/ui/StateSelect';
import TimezoneSelect from '../../../components/ui/TimezonSelect';
import { AppLayout } from '../../../components/layouts';
import PageHeader from '../../../components/dashboard/PageHeader';
import UserService from '../../../services/user.service';
import { setUserInfo } from '../../../redux/reducers/authSlice';
import { setCurrency } from '../../../redux/reducers/userSlice';
import { RootState } from '../../../redux/rootReducer';
import AuthPrimaryButton from '../../../components/ui/AuthPrimaryButton';

const labelStyle = 'mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary';
const inputShell =
    'w-full rounded-[12px] border border-[#E8E0EC] bg-white px-4 py-3 text-[14px] text-[#334155] outline-none focus:border-ryd-primary focus:ring-2 focus:ring-ryd-primary/15';

export default function ProfilePage() {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const userService = new UserService();
    const dispatch = useDispatch();

    const [loadingPersonal, setLoadingPersonal] = useState(false);
    const [loadingPassword, setLoadingPassword] = useState(false);
    const [loadingPrefs, setLoadingPrefs] = useState(false);
    const [country, setCountry] = useState<any>(null);
    const [passwordData, setPasswordData] = useState({
        passwordOld: '',
        password1: '',
        password2: '',
    });
    const [personalData, setPersonalData] = useState({
        firstName: userInfo?.firstName || '',
        lastName: userInfo?.lastName || '',
        country: userInfo?.country || '',
        state: userInfo?.state || '',
        timezone: userInfo?.timezone || '',
    });
    const [commPrefs, setCommPrefs] = useState({
        smsOptOut: !!userInfo?.smsOptOut,
        emailOptOut: !!userInfo?.emailOptOut || userInfo?.dnd === 1,
    });

    const getCurrency = async () => {
        try {
            const response = await userService.getCurrency();
            if (!response.status) return;
            dispatch(setCurrency(response.data));
        } catch {
            /* ignore */
        }
    };

    const handlePersonalInfoUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoadingPersonal(true);
        try {
            const response = await userService.profileUpdate(personalData);
            setLoadingPersonal(false);
            if (!response?.status) {
                toast.error(response?.message);
                return;
            }
            dispatch(setUserInfo(response.data));
            getCurrency();
            toast.success(response?.message || 'Profile updated');
        } catch (err: any) {
            setLoadingPersonal(false);
            toast.error(err.message);
        }
    };

    const handleCommPrefsUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoadingPrefs(true);
        try {
            const response = await userService.profileUpdate({
                smsOptOut: commPrefs.smsOptOut,
                emailOptOut: commPrefs.emailOptOut,
            } as any);
            setLoadingPrefs(false);
            if (!response?.status) {
                toast.error(response?.message);
                return;
            }
            dispatch(setUserInfo(response.data));
            setCommPrefs({
                smsOptOut: !!response.data?.smsOptOut,
                emailOptOut: !!response.data?.emailOptOut,
            });
            toast.success(response?.message || 'Communication preferences updated');
        } catch (err: any) {
            setLoadingPrefs(false);
            toast.error(err.message);
        }
    };

    const handlePasswordUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (passwordData.password1 !== passwordData.password2) {
            toast.error('New passwords must match');
            return;
        }
        setLoadingPassword(true);
        try {
            const response = await userService.passwordUpdate(passwordData);
            setLoadingPassword(false);
            if (!response?.status) {
                toast.error(response?.message);
                return;
            }
            setPasswordData({ passwordOld: '', password1: '', password2: '' });
            if (userInfo?.mustResetPassword) {
                dispatch(setUserInfo({ ...userInfo, mustResetPassword: false }));
            }
            toast.success(response?.message || 'Password updated');
        } catch (err: any) {
            setLoadingPassword(false);
            toast.error(err.message);
        }
    };

    return (
        <AppLayout>
            <PageHeader
                title="Profile"
                subtitle="Manage your account details, location preferences, and password."
            />

            <div className="mx-auto max-w-4xl space-y-8">
                {/* Personal Information section */}
                <section>
                    <div className="mb-4 flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-ryd-primaryLess1 text-ryd-primary">
                            <UserRound size={18} />
                        </div>
                        <div>
                            <h3 className="font-[AvertaStd-Semibold] text-[18px] text-ryd-headerTextPrimary">
                                Personal Information
                            </h3>
                            <p className="text-[13px] text-[#8b9aab]">Update your name and location preferences.</p>
                        </div>
                    </div>

                    <form className="space-y-4" onSubmit={handlePersonalInfoUpdate}>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className={labelStyle}>First Name</label>
                                <CustomInput
                                    type="text"
                                    placeholder={userInfo?.firstName}
                                    onChange={(e: any) =>
                                        setPersonalData({ ...personalData, firstName: e.target.value })
                                    }
                                />
                            </div>
                            <div>
                                <label className={labelStyle}>Last Name</label>
                                <CustomInput
                                    type="text"
                                    placeholder={userInfo?.lastName}
                                    onChange={(e: any) =>
                                        setPersonalData({ ...personalData, lastName: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        <div>
                            <label className={labelStyle}>Country</label>
                            <CountrySelectInput
                                placeholder={userInfo?.country}
                                handleCountryChange={(data: any) => {
                                    setCountry(data);
                                    setPersonalData({
                                        ...personalData,
                                        country: data.name,
                                        state: data?.states?.[0]?.name,
                                        timezone: data?.timezones?.[0]?.zoneName,
                                    });
                                }}
                                className={inputShell}
                            />
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className={labelStyle}>Province / State</label>
                                <StateSelectInput
                                    placeholder={userInfo?.state}
                                    country={country}
                                    handleStateChange={(data: any) =>
                                        setPersonalData({ ...personalData, state: data.name })
                                    }
                                    className={inputShell}
                                />
                            </div>
                            <div>
                                <label className={labelStyle}>Study Timezone</label>
                                <TimezoneSelect
                                    placeholder={userInfo?.timezone}
                                    country={country}
                                    handleTimezoneChange={(data: any) =>
                                        setPersonalData({ ...personalData, timezone: data.zoneName })
                                    }
                                    className={inputShell}
                                />
                            </div>
                        </div>

                        <div className="pt-1 sm:max-w-[220px]">
                            <AuthPrimaryButton loading={loadingPersonal} showArrow={false}>
                                Save changes
                            </AuthPrimaryButton>
                        </div>
                    </form>
                </section>

                <div className="border-t border-[#efe6f1]" />

                {/* Communication preferences */}
                <section>
                    <div className="mb-4 flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-ryd-primaryLess1 text-ryd-primary">
                            <Bell size={18} />
                        </div>
                        <div>
                            <h3 className="font-[AvertaStd-Semibold] text-[18px] text-ryd-headerTextPrimary">
                                Communication Preferences
                            </h3>
                            <p className="text-[13px] text-[#8b9aab]">
                                Choose whether to opt out of SMS and email messages from RYD Learning.
                            </p>
                        </div>
                    </div>

                    <form className="space-y-4" onSubmit={handleCommPrefsUpdate}>
                        <label className="flex items-start gap-2.5 rounded-[12px] border border-[#E8E0EC] bg-white px-4 py-3 text-[14px] text-[#476788]">
                            <input
                                type="checkbox"
                                checked={commPrefs.smsOptOut}
                                onChange={(e) =>
                                    setCommPrefs({ ...commPrefs, smsOptOut: e.target.checked })
                                }
                                className="mt-0.5 accent-ryd-primary"
                            />
                            <span>
                                <span className="block font-semibold text-ryd-headerTextPrimary">Opt out of SMS</span>
                                Stop receiving SMS updates about classes, reminders, and account notices.
                            </span>
                        </label>

                        <label className="flex items-start gap-2.5 rounded-[12px] border border-[#E8E0EC] bg-white px-4 py-3 text-[14px] text-[#476788]">
                            <input
                                type="checkbox"
                                checked={commPrefs.emailOptOut}
                                onChange={(e) =>
                                    setCommPrefs({ ...commPrefs, emailOptOut: e.target.checked })
                                }
                                className="mt-0.5 accent-ryd-primary"
                            />
                            <span>
                                <span className="block font-semibold text-ryd-headerTextPrimary">Opt out of Email</span>
                                Stop receiving marketing and reminder emails from RYD Learning.
                            </span>
                        </label>

                        <div className="pt-1 sm:max-w-[260px]">
                            <AuthPrimaryButton loading={loadingPrefs} showArrow={false}>
                                Save preferences
                            </AuthPrimaryButton>
                        </div>
                    </form>
                </section>

                <div className="border-t border-[#efe6f1]" />

                {/* Security section */}
                <section>
                    <div className="mb-4 flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-ryd-primaryLess1 text-ryd-primary">
                            <Lock size={18} />
                        </div>
                        <div>
                            <h3 className="font-[AvertaStd-Semibold] text-[18px] text-ryd-headerTextPrimary">
                                Security
                            </h3>
                            <p className="text-[13px] text-[#8b9aab]">Change your password to keep your account safe.</p>
                        </div>
                    </div>

                    <form className="max-w-md space-y-4" onSubmit={handlePasswordUpdate}>
                        <div>
                            <label className={labelStyle}>Current Password</label>
                            <CustomInput
                                type="password"
                                placeholder="••••••••"
                                required
                                onChange={(e: any) =>
                                    setPasswordData({ ...passwordData, passwordOld: e.target.value })
                                }
                            />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className={labelStyle}>New Password</label>
                                <CustomInput
                                    type="password"
                                    placeholder="••••••••"
                                    required
                                    onChange={(e: any) =>
                                        setPasswordData({ ...passwordData, password1: e.target.value })
                                    }
                                />
                            </div>
                            <div>
                                <label className={labelStyle}>Confirm Password</label>
                                <CustomInput
                                    type="password"
                                    placeholder="••••••••"
                                    required
                                    onChange={(e: any) =>
                                        setPasswordData({ ...passwordData, password2: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                        <div className="pt-1 sm:max-w-[220px]">
                            <AuthPrimaryButton loading={loadingPassword} showArrow={false}>
                                Update password
                            </AuthPrimaryButton>
                        </div>
                    </form>
                </section>
            </div>
        </AppLayout>
    );
}
